import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Stack, Typography,
} from '@mui/material';
import {
  EditOutlined as EditIcon,
  FolderOpenOutlined as FileIcon,
} from '@mui/icons-material';
import VehicleService, { Vehicle } from '../../api/services/vehicle.service';
import DocumentService, { RosterRow } from '../../api/services/document.service';
import { useAuthStore } from '../../store/authStore';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import ToneChip from '../../components/ui/ToneChip';
import { EmptyState, LoadingState } from '../../components/ui/States';
import FleetPanel, { FleetFilter } from './FleetPanel';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import { Eyebrow } from '../payroll/payrollUI';
import { toneForObligation, labelForObligation } from '../../components/files/vaultStatus';
import VehicleDialog from './VehicleDialog';

// One truck: what it is, its registration, what it costs, and whether its
// paperwork is in order. The same shape as a person's page in Users.
//
// The fleet list used to open the truck's FILE (its documents) because
// nothing else existed; the details imported from the vehicle sheet had
// nowhere to be read. Files is one button away from here.

const fmtDate = (iso: string | null | undefined) => (iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  : '—');
const fmtMoney = (n: number | null | undefined) => (n == null
  ? '—'
  : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Past is a problem, inside 30 days a warning — the vault's thresholds.
const dateTone = (iso: string | null | undefined): 'warn' | 'danger' | undefined => {
  if (!iso) return undefined;
  const days = (new Date(iso).getTime() - Date.now()) / 86400000;
  if (days < 0) return 'danger';
  if (days < 30) return 'warn';
  return undefined;
};

const Row: React.FC<{ label: string; children: React.ReactNode; tone?: 'warn' | 'danger'; mono?: boolean }> = ({
  label, children, tone, mono,
}) => (
  <Stack
    direction="row" justifyContent="space-between" alignItems="baseline" gap={2}
    sx={{ py: 0.75, borderBottom: '1px dashed', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere',
        color: tone === 'danger' ? 'error.dark' : tone === 'warn' ? 'warning.dark' : 'text.primary',
        ...(mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } : {}),
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Card sx={{ p: 2.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
      <Eyebrow>{title}</Eyebrow>
      {action}
    </Stack>
    {children}
  </Card>
);

/** "24 of 72" — whole months since the first payment, capped at the term. */
function paymentsMade(startedOn?: string | null, termMonths?: number): string | null {
  if (!startedOn || !termMonths) return null;
  const start = new Date(startedOn);
  const now = new Date();
  const months = (now.getUTCFullYear() - start.getUTCFullYear()) * 12 + (now.getUTCMonth() - start.getUTCMonth());
  const made = Math.max(0, Math.min(termMonths, months));
  return `${made} of ${termMonths}`;
}

const OWNERSHIP_LABEL = { cash: 'Owned (cash)', financed: 'Financed', leased: 'Leased' } as const;

const VehicleRecord: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { canArea } = useAuthStore();
  const canSeeMoney = canArea('fleetCost');

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [fleet, setFleet] = useState<Vehicle[]>([]);
  const [paperwork, setPaperwork] = useState<RosterRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const [v, roster, all] = await Promise.all([
        VehicleService.get(vehicleId),
        DocumentService.getRoster('vehicle', true).catch(() => [] as RosterRow[]),
        // For the panel's counts, which must match the list's.
        VehicleService.list({ includeRetired: true }).catch(() => [] as Vehicle[]),
      ]);
      setVehicle(v);
      setFleet(all);
      setPaperwork(roster.find((row) => row.ownerId === vehicleId) || null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load this vehicle');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);
  useEffect(() => { load(); }, [load]);

  // The section crumb already says Fleet and links to it.
  usePageBreadcrumb(vehicle ? [{ label: vehicle.unitNumber }] : []);

  const counts = useMemo(() => ({
    active: fleet.filter((t) => t.status === 'active').length,
    maintenance: fleet.filter((t) => t.status === 'maintenance').length,
    retired: fleet.filter((t) => t.status === 'retired').length,
    all: fleet.length,
  }), [fleet]);

  // The same panel as the list. The truck's own group is the lit one, and
  // a filter or Add goes back to the list to do it there.
  const panel = (
    <FleetPanel
      counts={counts}
      filter={(vehicle?.status as FleetFilter | undefined) ?? null}
      onFilter={(f) => navigate(f === 'all' ? '/fleet' : `/fleet?show=${f}`)}
      onAdd={() => navigate('/fleet?add=1')}
    />
  );

  if (loading && !vehicle) return <PageBody>{panel}<LoadingState /></PageBody>;
  if (!vehicle) {
    return (
      <PageBody>
        {panel}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <EmptyState title="No such vehicle" description="It could not be found in your company." />
      </PageBody>
    );
  }

  const v = vehicle;
  const own = v.ownership;
  const filesLink = `/files/entity/vehicle/${v._id}`;
  const statusChip = v.status === 'retired'
    ? <ToneChip tone="neutral" label="Retired" />
    : v.status === 'maintenance'
      ? <ToneChip tone="warn" label="In maintenance" />
      : <ToneChip tone="ok" label="On the road" />;
  const onPaper = own?.kind === 'financed' || own?.kind === 'leased';
  const made = paymentsMade(own?.startedOn, own?.termMonths);

  return (
    <PageBody>
      {panel}
      <PageHeader
        title={(
          <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
            <span>{v.unitNumber}</span>
            {statusChip}
          </Stack>
        )}
        subtitle={[
          [v.year, v.make, v.model].filter(Boolean).join(' '),
          v.bodyType,
          v.serviceAreaId != null ? `Service area ${v.serviceAreaId}` : null,
        ].filter(Boolean).join(' · ') || undefined}
        actions={(
          <Stack direction="row" gap={1}>
            <Button variant="outlined" startIcon={<FileIcon />} component={RouterLink} to={filesLink}>Files</Button>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>Edit</Button>
          </Stack>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
        <Section title="Vehicle">
          <Row label="VIN" mono>{v.vin || '—'}</Row>
          <Row label="Year">{v.year || '—'}</Row>
          <Row label="Make">{v.make || '—'}</Row>
          <Row label="Model">{v.model || '—'}</Row>
          <Row label="Body type">{v.bodyType || '—'}</Row>
          <Row label="Fuel">{v.fuel || '—'}</Row>
          {v.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>{v.notes}</Typography>
          )}
        </Section>

        <Section title="Registration">
          <Row label="Plate">{v.licensePlate ? `${v.licensePlate}${v.plateState ? ` · ${v.plateState}` : ''}` : '—'}</Row>
          <Row label="Title number">{v.titleNumber || '—'}</Row>
          <Row label="Registration expires" tone={dateTone(v.registrationExpiresOn)}>{fmtDate(v.registrationExpiresOn)}</Row>
          <Row label="Registration received">{v.registrationReceived ? 'Yes' : 'No'}</Row>
          <Row label="Insurance renews" tone={dateTone(v.insuranceRenewsOn)}>{fmtDate(v.insuranceRenewsOn)}</Row>
          <Row label="Schedule B">{v.onScheduleB ? 'Yes' : 'No'}</Row>
        </Section>

        {canSeeMoney && (
          <Section title="Cost">
            <Row label="Ownership">{own?.kind ? OWNERSHIP_LABEL[own.kind] : '—'}</Row>
            <Row label={own?.kind === 'cash' ? 'Purchase price' : 'Invoice value'}>{fmtMoney(own?.invoiceValue)}</Row>
            {onPaper && (
              <>
                <Row label={own?.kind === 'leased' ? 'Lessor' : 'Lienholder'}>{own?.lienholder || '—'}</Row>
                <Row label="Monthly payment">{fmtMoney(own?.monthlyPayment)}</Row>
                <Row label="Payments made">{made || '—'}</Row>
                <Row label="Term">{own?.termMonths ? `${own.termMonths} months · ${fmtDate(own.startedOn)} – ${fmtDate(own.maturesOn)}` : '—'}</Row>
                <Row label="Rate">{own?.interestRate != null ? `${own.interestRate}%` : '—'}</Row>
                <Row label="Down payment">{fmtMoney(own?.downPayment)}</Row>
                <Row label="Financed">{fmtMoney(own?.amountFinanced)}</Row>
                <Row label="Tax financed">{fmtMoney(own?.taxFinanced)}</Row>
                <Row label="Total cost">{fmtMoney(own?.totalCost)}</Row>
              </>
            )}
            <Row label="Property tax">
              {v.propertyTaxAmount != null ? fmtMoney(v.propertyTaxAmount) : '—'}
              {v.propertyTaxAccount ? ` · ${v.propertyTaxAccount}` : ''}
            </Row>
            {own?.notes && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{own.notes}</Typography>
            )}
          </Section>
        )}

        <Section
          title="Paperwork"
          action={<Button size="small" component={RouterLink} to={filesLink} sx={{ py: 0 }}>Open files</Button>}
        >
          {!paperwork || !paperwork.score || paperwork.score.total === 0 ? (
            <Typography variant="body2" color="text.secondary">Not tracked yet.</Typography>
          ) : (
            <>
              <Row label="On file">{`${paperwork.score.satisfied} of ${paperwork.score.total}`}</Row>
              <Row label="State">
                {!paperwork.worstStatus || paperwork.worstStatus === 'satisfied'
                  ? <Box component="span" sx={{ color: 'success.dark' }}>All on file</Box>
                  : (
                    <ToneChip
                      tone={toneForObligation(paperwork.worstStatus) === 'soon' ? 'warn' : 'danger'}
                      label={labelForObligation(paperwork.worstStatus)}
                      hollow={paperwork.worstStatus === 'missing'}
                    />
                  )}
              </Row>
            </>
          )}
        </Section>
      </Box>

      <VehicleDialog
        open={editing}
        vehicle={vehicle}
        onClose={() => setEditing(false)}
        onSaved={(saved) => { setEditing(false); setVehicle(saved); load(); }}
      />
    </PageBody>
  );
};

export default VehicleRecord;
